<template>
    <div
        v-show="visible"
        class="modal"
        role="dialog"
        aria-modal="true"
    >
        <div
            class="modal-backdrop"
            tabindex="-1"
            @click="close"
        />

        <div class="modal-frame">
            <div
                ref="contentRef"
                class="modal-content"
                role="document"
            >
                <slot />
                <ActionButton
                    label="Close"
                    :secondary="true"
                    @click="close"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onBeforeUnmount, watch } from 'vue'
import ActionButton from 'widget-components/components/ActionButton/index'

const visible = defineModel<boolean>()

const emit = defineEmits<{
    (e: 'close'): void
}>()

const close = () => {
    visible.value = false
    document.body.style.overflow = ''
    emit('close')
}

const contentRef = ref<HTMLElement | null>(null)

const handleKeyboard = (e: KeyboardEvent) => {
    if (!visible.value) {
        return
    }

    if (e.key === 'Escape') {
        e.preventDefault()
        close()
    }

    if (e.key === 'Tab') {
        const focusables = contentRef.value?.querySelectorAll<HTMLElement>(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        )

        if (!focusables || focusables.length === 0) {
            return
        }

        const first = focusables[0]
        const last = focusables[focusables.length - 1]

        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault()
            last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault()
            first.focus()
        }
    }
}

watch(visible, (v) => {
    if (v) {
        document.body.style.overflow = 'hidden'
        window.addEventListener('keydown', handleKeyboard)
    } else {
        window.removeEventListener('keydown', handleKeyboard)
    }
})

onBeforeUnmount(() => {
    window.removeEventListener('keydown', handleKeyboard)
})
</script>

<style scoped lang="sass">
.modal
    position: fixed
    inset: 0
    display: flex
    align-items: center
    justify-content: center
    z-index: var(--layer-component-popover, 1000)

.modal-backdrop
    position: absolute
    inset: 0
    background-color: rgba(0, 0, 0, 0.5)
    backdrop-filter: saturate(180%) blur(3px)

.modal-frame
    border-radius: var(--widget-border-radius)
    z-index: 1
    min-width: min(90vw, 600px)
    max-width: 90vw
    max-height: 90vh
    overflow: hidden
    display: flex
    flex-direction: column

.modal-content
    position: relative
    background: var(--tl-widget-background-color)
    padding: 20px
    width: 100%
    height: 100%
    overflow: auto
    flex: 1
</style>
